import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Edit3,
  FileClock,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { api, ApiError, query } from "../api";
import type {
  ApiList,
  LeaveRequest,
  LeaveType,
  Shift,
  ShiftChange,
  User,
} from "../types";
import {
  dateLabel,
  dateRangeLabel,
  LEAVE_LABELS,
  SHIFT_LABELS,
  STATUS_LABELS,
  todayIso,
} from "../utils";
import { SCHEDULE_PATTERNS, type SchedulePattern } from "../../shared/domain-types";
import { getShiftForPattern } from "../../shared/shifts";
import {
  Alert,
  Button,
  Empty,
  Field,
  Loading,
  Modal,
  PageHeader,
  SelectField,
  StatusBadge,
  TextareaField,
} from "../components/ui";

interface LeavePreview {
  chargeableDays: string[];
  excludedOffDays: string[];
  nonDeductedSundayDays?: string[];
  units: number;
  warnings?: string[];
  blocked?: boolean;
  message?: string;
  balanceSource?: "annual" | "excuse" | null;
}
interface ShiftPreview {
  currentShift: Shift;
  blocked?: boolean;
  message?: string;
  reason?: string | null;
  availableShifts?: Array<"SABAH" | "OGLE" | "GECE">;
}

function listOf<T>(value: ApiList<T> | T[] | { requests: T[] }): T[] {
  if (Array.isArray(value)) return value;
  if ("items" in value) return value.items;
  return value.requests;
}

const leaveTypes = Object.keys(LEAVE_LABELS) as LeaveType[];

const calendarWeekdays = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function monthKey(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.slice(0, 7) : todayIso().slice(0, 7);
}

function moveMonth(value: string, amount: number): string {
  const [year = 2026, month = 1] = value.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1 + amount, 1));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}`;
}

function rangeDateLabel(start: string, end: string): string {
  const label = (value: string) => format(parseISO(value), "d MMMM yyyy", { locale: tr });
  return `${label(start)} – ${label(end)}`;
}

function LeaveDateRangeCalendar({
  startDate,
  endDate,
  onChange,
  currentUser,
  leaves,
  editingId,
}: {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  currentUser: User;
  leaves: LeaveRequest[];
  editingId?: string | number | null;
}) {
  const [visibleMonth, setVisibleMonth] = useState(monthKey(startDate));
  const [awaitingEnd, setAwaitingEnd] = useState(Boolean(startDate && !endDate));
  const [selectionError, setSelectionError] = useState("");
  const rawPattern = currentUser.schedulePattern ?? currentUser.assignment?.schedulePattern;
  const pattern = SCHEDULE_PATTERNS.includes(rawPattern as SchedulePattern)
    ? (rawPattern as SchedulePattern)
    : null;
  const [year = 2026, month = 1] = visibleMonth.split("-").map(Number);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const leadingBlanks = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const dates = Array.from({ length: daysInMonth }, (_, index) =>
    `${visibleMonth}-${String(index + 1).padStart(2, "0")}`,
  );
  const monthLabel = new Intl.DateTimeFormat("tr-TR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  const activeLeaves = leaves.filter(
    (leave) =>
      leave.id !== editingId &&
      !["REJECTED", "CANCELLED", "SUPERSEDED"].includes(leave.status),
  );

  function selectDate(date: string) {
    setSelectionError("");
    if (!awaitingEnd || !startDate || endDate) {
      onChange(date, "");
      setAwaitingEnd(true);
      return;
    }
    if (date === startDate && pattern && getShiftForPattern(pattern, date) === "OFF") {
      setSelectionError("OFF günü tek günlük izin olarak seçilemez.");
      return;
    }
    onChange(date < startDate ? date : startDate, date < startDate ? startDate : date);
    setAwaitingEnd(false);
  }

  return (
    <section className="request-date-calendar" aria-label="İzin tarih aralığı seçimi">
      <header>
        <button type="button" onClick={() => setVisibleMonth((value) => moveMonth(value, -1))} aria-label="Önceki ay">
          <ChevronLeft />
        </button>
        <strong>{monthLabel}</strong>
        <button type="button" onClick={() => setVisibleMonth((value) => moveMonth(value, 1))} aria-label="Sonraki ay">
          <ChevronRight />
        </button>
      </header>
      <div className="request-date-calendar__weekdays" aria-hidden="true">
        {calendarWeekdays.map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="request-date-calendar__grid">
        {Array.from({ length: leadingBlanks }, (_, index) => <span key={`blank-${index}`} />)}
        {dates.map((date) => {
          const shift = pattern ? getShiftForPattern(pattern, date) : null;
          const isStart = date === startDate;
          const isEnd = Boolean(endDate) && date === endDate;
          const inRange = Boolean(endDate) && date >= startDate && date <= endDate;
          const conflict = activeLeaves.some((leave) => date >= leave.startDate && date <= leave.endDate);
          const classes = [
            isStart || isEnd ? "is-edge" : "",
            inRange && !isStart && !isEnd ? "is-range" : "",
            conflict ? "has-conflict" : "",
            shift === "OFF" ? "is-off" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              type="button"
              className={classes}
              key={date}
              onClick={() => selectDate(date)}
              aria-label={`${dateLabel(date)}${shift ? `, ${SHIFT_LABELS[shift]}` : ""}${conflict ? ", mevcut talep var" : ""}`}
            >
              <b>{Number(date.slice(-2))}</b>
              {shift ? <span className={`request-date-calendar__shift request-date-calendar__shift--${shift.toLowerCase()}`}>{SHIFT_LABELS[shift]}</span> : null}
              {conflict ? <i title="Mevcut aktif izin veya yokluk" /> : null}
            </button>
          );
        })}
      </div>
      <div className="request-date-calendar__selection" aria-live="polite">
        {startDate && endDate
          ? rangeDateLabel(startDate, endDate)
          : startDate
            ? `${dateLabel(startDate)} seçildi; bitiş tarihini seçin.`
            : "Başlangıç tarihini seçin."}
      </div>
      {selectionError ? <Alert tone="danger">{selectionError}</Alert> : null}
    </section>
  );
}

function LeaveForm({
  open,
  onClose,
  onSaved,
  editing,
  currentUser,
  leaves,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  editing?: LeaveRequest | null;
  currentUser: User;
  leaves: LeaveRequest[];
}) {
  const [type, setType] = useState<LeaveType>(editing?.type ?? "ANNUAL");
  const [startDate, setStartDate] = useState(editing?.startDate ?? "");
  const [endDate, setEndDate] = useState(editing?.endDate ?? "");
  const [duration, setDuration] = useState<"FULL" | "HALF" | "TWO_HOURS">(
    editing?.duration ?? "FULL",
  );
  const [partialTiming, setPartialTiming] = useState<
    "" | "LATE_ARRIVAL" | "EARLY_DEPARTURE"
  >(editing?.partialTiming ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [preview, setPreview] = useState<LeavePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const partialAllowed = type === "ANNUAL" || type === "EXCUSE";
  const twoHoursAllowed = type === "EXCUSE";

  useEffect(() => {
    if (!open) return;
    setType(editing?.type ?? "ANNUAL");
    setStartDate(editing?.startDate ?? "");
    setEndDate(editing?.endDate ?? "");
    setDuration(editing?.duration ?? "FULL");
    setPartialTiming(editing?.partialTiming ?? "");
    setDescription(editing?.description ?? "");
    setError("");
    setPreview(null);
  }, [open, editing?.id]);

  useEffect(() => {
    if (!open) return;
    if (!partialAllowed && duration !== "FULL") setDuration("FULL");
    if (!twoHoursAllowed && duration === "TWO_HOURS") setDuration("FULL");
  }, [type, duration, open, partialAllowed, twoHoursAllowed]);

  useEffect(() => {
    let active = true;
    if (!open || !startDate || !endDate || endDate < startDate) {
      setPreview(null);
      setPreviewing(false);
      return () => {
        active = false;
      };
    }
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      api<LeavePreview>("/api/leaves/preview", {
        method: "POST",
        body: {
          type,
          startDate,
          endDate,
          duration,
          partialTiming: partialTiming || null,
          ignoreLeaveId: editing?.id ?? null,
        },
      })
        .then((result) => {
          if (!active) return;
          setPreview(result);
          setError("");
        })
        .catch((caught) => {
          if (!active) return;
          if (caught instanceof ApiError && caught.status !== 404) {
            setError(caught.message);
            setPreview({
              chargeableDays: [],
              excludedOffDays: [],
              nonDeductedSundayDays: [],
              units: 0,
              blocked: true,
              message: caught.message,
            });
          } else {
            setPreview(null);
          }
        })
        .finally(() => {
          if (active) setPreviewing(false);
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    open,
    type,
    startDate,
    endDate,
    duration,
    partialTiming,
    editing?.id,
    editing?.status,
  ]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!startDate || !endDate)
      return setError("Başlangıç ve bitiş tarihini takvimden seçin.");
    if (endDate < startDate)
      return setError("Bitiş tarihi başlangıçtan önce olamaz.");
    if (duration !== "FULL" && startDate !== endDate)
      return setError(
        "Yarım gün ve iki saat yalnızca tek bir çalışma günü için seçilebilir.",
      );
    if (duration !== "FULL" && !partialTiming)
      return setError(
        "Kısmi izin için geç gelme veya erken çıkma seçilmelidir.",
      );
    if (preview?.blocked)
      return setError(
        preview.message ??
          "Seçilen günlerin tamamı OFF olduğu için talep oluşturulamaz.",
      );
    setLoading(true);
    const body = {
      type,
      startDate,
      endDate,
      duration,
      partialTiming: duration === "FULL" ? null : partialTiming,
      description: description.trim() || null,
    };
    try {
      if (editing?.status === "PENDING")
        await api(`/api/leaves/${editing.id}`, { method: "PATCH", body });
      else if (editing)
        await api(`/api/leaves/${editing.id}/change`, { method: "POST", body });
      else await api("/api/leaves", { method: "POST", body });
      onSaved(
        editing?.status === "PENDING"
          ? "Bekleyen talebiniz güncellendi."
          : editing
            ? "Değişiklik talebiniz gönderildi."
            : "İzin talebiniz gönderildi.",
      );
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Talep kaydedilemedi.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing?.status === "PENDING"
          ? "Bekleyen talebi düzenle"
          : editing
            ? "İzin değişikliği iste"
            : "Yeni izin talebi"
      }
      wide
    >
      <form onSubmit={submit} className="form-stack leave-request-form">
        <Alert>
          OFF günleri aralıktan otomatik çıkarılır. Yıllık izindeki Pazar
          günleri bakiyeden düşülmez. Bakiye yetersizliği talebinizi engellemez.
        </Alert>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="form-grid">
          <SelectField
            label="İzin türü"
            value={type}
            onChange={(event) => setType(event.target.value as LeaveType)}
          >
            {leaveTypes.map((item) => (
              <option value={item} key={item}>
                {LEAVE_LABELS[item]}
              </option>
            ))}
          </SelectField>
          <fieldset className="duration-field">
            <legend>Süre</legend>
            <div className="duration-segmented" role="group" aria-label="İzin süresi">
              <button type="button" className={duration === "FULL" ? "active" : ""} onClick={() => setDuration("FULL")}>Tam Gün</button>
              <button type="button" className={duration === "HALF" ? "active" : ""} onClick={() => setDuration("HALF")} disabled={!partialAllowed}>Yarım Gün</button>
              <button type="button" className={duration === "TWO_HOURS" ? "active" : ""} onClick={() => setDuration("TWO_HOURS")} disabled={!twoHoursAllowed}>2 Saat</button>
            </div>
          </fieldset>
          {duration !== "FULL" ? (
            <SelectField
              label="Kısmi izin biçimi"
              value={partialTiming}
              onChange={(event) =>
                setPartialTiming(event.target.value as typeof partialTiming)
              }
              required
            >
              <option value="">Seçin</option>
              <option value="LATE_ARRIVAL">Geç gelecek</option>
              <option value="EARLY_DEPARTURE">Erken çıkacak</option>
            </SelectField>
          ) : null}
        </div>
        <LeaveDateRangeCalendar
          startDate={startDate}
          endDate={endDate}
          currentUser={currentUser}
          leaves={leaves}
          editingId={editing?.id}
          onChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />
        <TextareaField
          label="Açıklama (isteğe bağlı)"
          value={description}
          maxLength={500}
          onChange={(event) => setDescription(event.target.value)}
          hint="Yalnızca siz, başteknisyeniniz, aktif vekil ve admin görebilir."
        />
        {previewing ? (
          <div className="inline-loading">
            <RefreshCw className="spin" />
            Günler hesaplanıyor
          </div>
        ) : preview ? (
          <div className="preview-box">
            <div>
              <span>{type === "ASSIGNMENT" ? "Görev izni — hiçbir izin bakiyesinden düşülmez" : "Bakiyeden düşecek izin"}</span>
              <strong>{preview.units} gün</strong>
            </div>
            {preview.excludedOffDays?.length ? (
              <div>
                <span>OFF olduğu için hesaba katılmayacak</span>
                <strong>
                  {preview.excludedOffDays
                    .map((date) => dateLabel(date, "short"))
                    .join(", ")}
                </strong>
              </div>
            ) : null}
            {preview.nonDeductedSundayDays?.length ? (
              <div>
                <span>Pazar olduğu için yıllık izin bakiyesinden düşülmeyecek</span>
                <strong>
                  {preview.nonDeductedSundayDays
                    .map((date) => dateLabel(date, "short"))
                    .join(", ")}
                </strong>
              </div>
            ) : null}
            {preview.warnings?.map((warning) => (
              <Alert tone="warning" key={warning}>
                {warning}
              </Alert>
            ))}
            {preview.message ? (
              <Alert tone={preview.blocked ? "danger" : "warning"}>
                {preview.message}
              </Alert>
            ) : null}
          </div>
        ) : null}
        <div className="modal-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button type="submit" loading={loading} disabled={preview?.blocked}>
            {editing?.status === "PENDING" ? "Değişiklikleri kaydet" : "Talebi gönder"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ShiftForm({
  open,
  onClose,
  onSaved,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  editing?: ShiftChange | null;
}) {
  const [date, setDate] = useState(editing?.date ?? todayIso());
  const [requestedShift, setRequestedShift] = useState<
    "SABAH" | "OGLE" | "GECE"
  >(editing?.requestedShift ?? "SABAH");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [preview, setPreview] = useState<ShiftPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setDate(editing?.date ?? todayIso());
    setRequestedShift(editing?.requestedShift ?? "SABAH");
    setDescription(editing?.description ?? "");
    setPreview(null);
    setError("");
  }, [open, editing?.id]);
  useEffect(() => {
    if (!open || !date) return;
    let active = true;
    api<ShiftPreview>(
      `/api/shift-changes/preview${query({
        date,
        ignoreShiftChangeId:
          editing?.status === "PENDING" ? editing.id : undefined,
      })}`,
    )
      .then((result) => {
        if (!active) return;
        setPreview(result);
        setError("");
        const first = result.availableShifts?.[0];
        if (first) {
          setRequestedShift((current) =>
            result.availableShifts?.includes(current) ? current : first,
          );
        }
      })
      .catch((caught) => {
        if (!active) return;
        if (caught instanceof ApiError && caught.status !== 404)
          setError(caught.message);
        setPreview(null);
      });
    return () => {
      active = false;
    };
  }, [open, date, editing?.id, editing?.status]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (preview?.blocked || preview?.currentShift === "OFF")
      return setError(
        preview.message ??
          preview.reason ??
          "OFF gününde vardiya değişikliği istenemez.",
      );
    if (preview?.currentShift === requestedShift)
      return setError("Yeni vardiya mevcut vardiyadan farklı olmalıdır.");
    setLoading(true);
    try {
      await api(
        editing ? `/api/shift-changes/${editing.id}` : "/api/shift-changes",
        {
          method: editing ? "PATCH" : "POST",
          body: {
            date,
            requestedShift,
            description: description.trim() || null,
          },
        },
      );
      onSaved(
        editing
          ? "Bekleyen vardiya değişikliği talebiniz güncellendi."
          : "Vardiya değişikliği talebiniz gönderildi.",
      );
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Talep kaydedilemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing
          ? "Vardiya değişikliği talebini düzenle"
          : "Vardiya değişikliği talebi"
      }
    >
      <form className="form-stack" onSubmit={submit}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <Field
          label="Çalışma tarihi"
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          required
        />
        {preview ? (
          <div className="current-shift">
            <span>Hesaplanan mevcut vardiya</span>
            <strong>{SHIFT_LABELS[preview.currentShift]}</strong>
            {preview.message || preview.reason ? (
              <small>{preview.message ?? preview.reason}</small>
            ) : null}
          </div>
        ) : null}
        <SelectField
          label="Talep edilen yeni vardiya"
          value={requestedShift}
          onChange={(event) =>
            setRequestedShift(event.target.value as typeof requestedShift)
          }
        >
          {(["SABAH", "OGLE", "GECE"] as const)
            .filter(
              (shift) =>
                !preview?.availableShifts ||
                preview.availableShifts.includes(shift),
            )
            .map((shift) => (
              <option value={shift} key={shift}>
                {SHIFT_LABELS[shift]}
              </option>
            ))}
        </SelectField>
        <TextareaField
          label="Açıklama (isteğe bağlı)"
          maxLength={300}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <div className="modal-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={preview?.blocked || preview?.currentShift === "OFF"}
          >
            {editing ? "Değişiklikleri kaydet" : "Talebi gönder"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function RequestsScreen({
  currentUser,
  create,
  notify,
}: {
  currentUser: User;
  create?: "leave" | "shift" | null;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const allowShiftChanges = currentUser.role !== "supervisor";
  const [tab, setTab] = useState<"leave" | "shift">(
    create === "shift" ? "shift" : "leave",
  );
  const [leaveOpen, setLeaveOpen] = useState(create === "leave");
  const [shiftOpen, setShiftOpen] = useState(create === "shift");
  const [editing, setEditing] = useState<LeaveRequest | null>(null);
  const [editingShift, setEditingShift] = useState<ShiftChange | null>(null);
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [shifts, setShifts] = useState<ShiftChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const focusedId = new URLSearchParams(window.location.hash.split("?", 2)[1] ?? "").get("focus");
  async function load() {
    setLoading(true);
    setError("");
    try {
      const [leaveData, shiftData] = await Promise.all([
        api<
          ApiList<LeaveRequest> | LeaveRequest[] | { requests: LeaveRequest[] }
        >(`/api/leaves${query({ mine: true })}`),
        api<ApiList<ShiftChange> | ShiftChange[] | { requests: ShiftChange[] }>(
          `/api/shift-changes${query({ mine: true })}`,
        ),
      ]);
      setLeaves(listOf(leaveData));
      setShifts(listOf(shiftData));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Talepler yüklenemedi.",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (loading || !focusedId) return;
    if (shifts.some((item) => String(item.id) === focusedId)) setTab("shift");
    else if (leaves.some((item) => String(item.id) === focusedId)) setTab("leave");
    window.setTimeout(() => document.getElementById(`request-${focusedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  }, [focusedId, leaves, loading, shifts]);
  async function cancel(
    kind: "leave" | "shift-change",
    id: string | number,
    approved: boolean,
  ) {
    const text = approved
      ? "Onaylı kayıt için iptal talebi gönderilsin mi?"
      : "Bekleyen talep iptal edilsin mi?";
    if (!window.confirm(text)) return;
    try {
      await api(
        `/api/${kind === "leave" ? "leaves" : "shift-changes"}/${id}/cancel`,
        { method: "POST" },
      );
      notify(
        approved ? "İptal talebiniz gönderildi." : "Talebiniz iptal edildi.",
      );
      await load();
    } catch (caught) {
      notify(
        caught instanceof ApiError
          ? caught.message
          : "İptal işlemi yapılamadı.",
        "danger",
      );
    }
  }
  function saved(message: string) {
    notify(message);
    void load();
  }
  const activeLeaves = useMemo(
    () => [...leaves].sort((a, b) => b.startDate.localeCompare(a.startDate)),
    [leaves],
  );

  return (
    <div className="page">
      <PageHeader
        eyebrow="KİŞİSEL"
        title="Taleplerim"
        description={`${currentUser.displayName} adına oluşturduğunuz kayıtlar.`}
        actions={
          <div className="button-row">
            <Button
              onClick={() => {
                setEditing(null);
                setLeaveOpen(true);
              }}
            >
              <Plus size={18} />
              İzin talebi
            </Button>
            {allowShiftChanges ? <Button
              variant="secondary"
              onClick={() => {
                setEditingShift(null);
                setShiftOpen(true);
              }}
            >
              <RefreshCw size={18} />
              Vardiya değişikliği
            </Button> : null}
          </div>
        }
      />
      <div className="segmented request-tabs">
        <button
          className={tab === "leave" ? "active" : ""}
          onClick={() => setTab("leave")}
        >
          <CalendarCheck />
          İzinler <span>{leaves.length}</span>
        </button>
        {allowShiftChanges ? <button
          className={tab === "shift" ? "active" : ""}
          onClick={() => setTab("shift")}
        >
          <RefreshCw />
          Vardiya değişiklikleri <span>{shifts.length}</span>
        </button> : null}
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? (
        <Loading label="Talepleriniz yükleniyor" />
      ) : tab === "leave" ? (
        <div className="request-list">
          {activeLeaves.length ? (
            activeLeaves.map((leave) => (
              <article id={`request-${leave.id}`} className={`request-card ${String(leave.id) === focusedId ? "record-card--focused" : ""}`} key={leave.id}>
                <header>
                  <div className="request-icon">
                    <CalendarCheck />
                  </div>
                  <div>
                    <strong>
                      {leave.requestKind === "change"
                        ? `Değişiklik talebi · ${LEAVE_LABELS[leave.type]}`
                        : LEAVE_LABELS[leave.type]}
                    </strong>
                    <span>
                      {dateRangeLabel(leave.startDate, leave.endDate)}
                    </span>
                  </div>
                  <StatusBadge status={leave.status} />
                </header>
                <dl>
                  <div>
                    <dt>Süre</dt>
                    <dd>{leave.units} gün</dd>
                  </div>
                  <div>
                    <dt>Durum</dt>
                    <dd>{STATUS_LABELS[leave.status]}</dd>
                  </div>
                  {leave.approverName ? (
                    <div>
                      <dt>İşlem yapan</dt>
                      <dd>
                        {leave.approverName}
                        {leave.actedAsDelegate ? " (vekâleten)" : ""}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {leave.description ? (
                  <p className="private-description">
                    <span>Açıklamanız</span>
                    {leave.description}
                  </p>
                ) : null}
                {leave.warning ? (
                  <Alert tone="warning">{leave.warning}</Alert>
                ) : null}
                <footer>
                  {leave.status === "PENDING" ? (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setEditing(leave);
                          setLeaveOpen(true);
                        }}
                      >
                        <Edit3 size={16} />
                        {leave.requestKind === "change"
                          ? "Değişikliği düzenle"
                          : "Düzenle"}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => void cancel("leave", leave.id, false)}
                      >
                        <Trash2 size={16} />
                        {leave.requestKind === "change"
                          ? "Değişiklik isteğini geri çek"
                          : "İptal et"}
                      </Button>
                    </>
                  ) : leave.status === "APPROVED" ? (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setEditing(leave);
                          setLeaveOpen(true);
                        }}
                      >
                        <Edit3 size={16} />
                        Değişiklik iste
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => void cancel("leave", leave.id, true)}
                      >
                        <Trash2 size={16} />
                        İptal iste
                      </Button>
                    </>
                  ) : null}
                </footer>
              </article>
            ))
          ) : (
            <Empty
              icon={<FileClock />}
              title="İzin talebiniz yok"
              text="İlk izin talebinizi oluşturabilirsiniz."
            />
          )}
        </div>
      ) : (
        <div className="request-list">
          {shifts.length ? (
            shifts.map((shift) => (
              <article id={`request-${shift.id}`} className={`request-card ${String(shift.id) === focusedId ? "record-card--focused" : ""}`} key={shift.id}>
                <header>
                  <div className="request-icon">
                    <RefreshCw />
                  </div>
                  <div>
                    <strong>
                      {SHIFT_LABELS[shift.requestedShift]} gelecek
                    </strong>
                    <span>{dateLabel(shift.date)}</span>
                  </div>
                  <StatusBadge status={shift.status} />
                </header>
                <dl>
                  <div>
                    <dt>Hesaplanan</dt>
                    <dd>{SHIFT_LABELS[shift.currentShift]}</dd>
                  </div>
                  <div>
                    <dt>Talep edilen</dt>
                    <dd>{SHIFT_LABELS[shift.requestedShift]}</dd>
                  </div>
                </dl>
                {shift.description ? (
                  <p className="private-description">
                    <span>Açıklamanız</span>
                    {shift.description}
                  </p>
                ) : null}
                {shift.status === "PENDING" || shift.status === "APPROVED" ? (
                  <footer>
                    {shift.status === "PENDING" ? (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setEditingShift(shift);
                          setShiftOpen(true);
                        }}
                      >
                        <Edit3 size={16} />
                        Düzenle
                      </Button>
                    ) : null}
                    <Button
                      variant="danger"
                      onClick={() =>
                        void cancel(
                          "shift-change",
                          shift.id,
                          shift.status === "APPROVED",
                        )
                      }
                    >
                      <Trash2 size={16} />
                      {shift.status === "APPROVED" ? "İptal iste" : "İptal et"}
                    </Button>
                  </footer>
                ) : null}
              </article>
            ))
          ) : (
            <Empty
              icon={<RefreshCw />}
              title="Vardiya değişikliği yok"
              text="Kendi çalışma gününüz için tek kişilik değişiklik isteyebilirsiniz."
            />
          )}
        </div>
      )}
      <LeaveForm
        open={leaveOpen}
        editing={editing}
        currentUser={currentUser}
        leaves={leaves}
        onClose={() => {
          setLeaveOpen(false);
          setEditing(null);
        }}
        onSaved={saved}
      />
      {allowShiftChanges ? <ShiftForm
        open={shiftOpen}
        editing={editingShift}
        onClose={() => {
          setShiftOpen(false);
          setEditingShift(null);
        }}
        onSaved={saved}
      /> : null}
    </div>
  );
}
